import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { AdminBaseEditComponent } from 'src/modules/sm-base/components/admin-base-edit/admin-base-edit.component';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { FrontendFieldValueChangedEvent } from 'src/modules/sm-base/models/frontend-field-value-changed-event.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { FrontendFieldError } from 'src/modules/sm-base/netgen/FrontendFieldError';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from 'src/modules/utils/shared/dates';
import { Objects } from 'src/modules/utils/shared/objects';
import { Utils } from 'src/modules/utils/shared/utils';
import { ProjectPlanningTools } from '../../models/project-planning-tools.model';
import { CalendarEvent } from '../../netgen/CalendarEvent';
import { CalendarEventKind } from '../../netgen/CalendarEventKind';
import { CalendarEventResource } from '../../netgen/CalendarEventResource';
import { CalendarEventStartRelativeTo } from '../../netgen/CalendarEventStartRelativeTo';
import { CalendarEventSubTask } from '../../netgen/CalendarEventSubTask';
import { CalendarEventTemplate } from '../../netgen/CalendarEventTemplate';
import { CalendarEventType } from '../../netgen/CalendarEventType';
import { CalendarRole } from '../../netgen/CalendarRole';
import { CrudWithPreviewDto } from '../../netgen/CrudWithPreviewDto';
import { Project } from '../../netgen/Project';
import { Resource } from '../../netgen/Resource';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { CalendarEventRole } from '../../netgen/CalendarEventRole';
import { SaveDataHandler } from 'src/modules/app-template/models/save-data-handler.model';

@Component({
    selector: 'app-edit-calendar-event',
    templateUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.html',
    styleUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditCalendarEventForProjectComponent extends AdminBaseEditComponent<CalendarEvent> {

    service = inject(ProjectPlanningService);

    forProjectId: number = null;

    definition = ProjectPlanningTools.definitionCalendarEvent;

    project: Project;
    eventsOfProject: CalendarEvent[];
    projects: Project[];
    eventTemplates: CalendarEventTemplate[];
    eventTypes: CalendarEventType[];
    users: DatabaseUser[];
    roles: CalendarRole[];
    resources: Resource[];
    isFinished = false;
    editAllowed = false;
    isCopy = false;
    copy: CalendarEvent;

    getForm(): FrontendForm {
        this.editAllowed = this.isInsert() || this.item.isEditAllowed(this.service.getCurrentUser());
        return this.app.tForm("entity.CalendarEvent.fields", new FrontendForm([
            !this.isInsert() ? new FrontendField("info", this.app.t("entity.CalendarEvent.other.projectInfo", { title: this.project.title, projectNumber: this.project.projectNumber }), FrontendFieldType.string, {
                hasControl: false
            }) : null,
            this.isInsert() ? new FrontendField("projectId", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                listItems: FrontendFieldListItem.make(this.projects, item => [item.id, item.title, { html: true, labelInDropdown: item.getHtmlTitle(true), comparable: (item.finished ? "1" : "0") + item.title, projectNumber: item.projectNumber}]),
                dropdownCanFilter: true,
                dropdownFilterBy: "label,projectNumber",
                //TODO undefined muss hier aufgezählt werdne, weil projectId = undefined ist bei Neuanlage und das sonst nicht als "nicht befüllt" erkannt wird
                emptyValues: [null, undefined, 0],
                onValueChanged: this.projectIdChanged.bind(this)
            }) : null,
            new FrontendField("kind", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                listItems: FrontendFieldListItem.make(Utils.getEnumValues(CalendarEventKind).filter(item => item !=
                                (CalendarEventKind.appointment as number),
                        ),item => [item, CalendarEventKind.getDisplayName(item, this.app)], false),
                guiGroup: "kind",
                guiWidthInGroup: 50,
                onValueChanged: this.eventTypeChanged.bind(this),
                callValueChangedAfterLoad: true
            }),
            this.isInsert() ? new FrontendField("template", this.app.t("entity.CalendarEvent.other.template"), FrontendFieldType.dropdown, { 
                listItems: FrontendFieldListItem.make(this.eventTemplates.filter(t => t.forStandalone), item => [item.id, item.title], true, [[null, this.app.t("entity.CalendarEvent.other.noTemplate")]]),
                dropdownCanFilter: true,
                guiGroup: "kind",
                guiWidthInGroup: 50,
                onValueChanged: this.eventTemplateChanged.bind(this)
            }) : null,
            new FrontendField("title", "", FrontendFieldType.string, { 
                mandatory: true, 
                maxLength: 100,
                guiGroup: "title", 
                guiWidthInGroup: 50
            }),
            new FrontendField("typeId", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                guiGroup: "title", 
                guiWidthInGroup: 50,
                listItems: FrontendFieldListItem.make(this.eventTypes, item => [item.id, item.title], true),
                //TODO undefined muss hier aufgezählt werdne, weil projectId = undefined ist bei Neuanlage und das sonst nicht als "nicht befüllt" erkannt wird
                emptyValues: [null, undefined, 0],
                dropdownCanFilter: true
            }),
            new FrontendField("structurePath", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                listItems: FrontendFieldListItem.make(this.project?.structurePaths ?? [], item => [item, this.service.formatStructurePathString(item)], true),
                dropdownCanFilter: true
            }),
            new FrontendField("note", "", FrontendFieldType.textArea, { 
                fixedHeight: "100px"
            }),
            new FrontendField("createdBy", "", FrontendFieldType.string, { 
                enabled: false,
                guiGroup: "createdBy", 
                guiWidthInGroup: 1
            }),
            new FrontendField("contactPersonId", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                guiGroup: "createdBy", 
                guiWidthInGroup: 1,
                listItems: FrontendFieldListItem.make(this.users, item => [item.id, item.getFullName()], true),
                dropdownCanFilter: true
            }),
            new FrontendField("watchersIds", "", FrontendFieldType.dropdownMulti, { 
                listItems: FrontendFieldListItem.make(this.users, item => [item.id, item.getFullName()], true),
                dropdownCanFilter: true
            }),
            new FrontendField("subTasks", "", FrontendFieldType.table, {
                tableColumns: [
                    new TableColumn("title", this.app.t("entity.CalendarEventSubTask.fields.title"), TableCellType.string, 100, {
                        editable: true
                    }),
                    new TableColumn("finished", this.app.t("entity.CalendarEventSubTask.fields.finished"), TableCellType.bool, 1, {
                        editable: true
                    })    
                ],
                fixedHeight: "200px",
                tableCanAddRow: true,
                tableCanRemoveRow: true,
                generateEmptyTableItem: () => Objects.fromPlain(CalendarEventSubTask, {
                    title: "",
                    finished: false
                })
            }),
            new FrontendField("onlyWeekdays", "", FrontendFieldType.bool),
            new FrontendField("startRelativeDays", "", FrontendFieldType.int, {
                guiGroup: "startRelativeDays", 
                guiWidthInGroup: 1,
                minValue: null,
                onValueChanged: this.startRelativeDaysChanged.bind(this)
            }),
            new FrontendField("startRelativeTo", "", FrontendFieldType.dropdown, {
                guiGroup: "startRelativeDays", 
                guiWidthInGroup: 1,
                dropdownCanFilter: true,
                listItems: this.getStartRelativeToEventIdListItems(),
                toForm: (value, on) => {
                    let e = on as CalendarEvent;
                    if (e.startRelativeTo == CalendarEventStartRelativeTo.task) {
                        return this.eventsOfProject.find(evt => evt.id == e.startRelativeToEventId);
                    }          
                    else {
                        return e.startRelativeTo;
                    }
                },
                fromForm: (value, on) => {
                    let e = on as CalendarEvent;
                    if (value instanceof CalendarEvent) {
                        e.startRelativeToEventId = value.id;
                        e.startRelativeTo = CalendarEventStartRelativeTo.task;
                    }
                    else {
                        e.startRelativeToEventId = null;
                        e.startRelativeTo = value as CalendarEventStartRelativeTo;
                    }
                    return e.startRelativeTo;
                },
                onValueChanged: this.startRelativeToChanged.bind(this)
            }),
            new FrontendField("startDate", "", FrontendFieldType.date, { 
                mandatory: true,
                guiGroup: "startDate", 
                guiWidthInGroup: 1,
                dateIntervalToField: "endDate",
                dateIntervalEmptyAllowed: false,
                onValueChanged: this.startDateChanged.bind(this),
                validator: (field, form) => {
                    let startDate = field.getValueDate();
                    if (form.getValueBool("onlyWeekdays") && startDate != null && !Dates.isWeekday(startDate)) {
                        return [new FrontendFieldError(field.id, this.app.t("entity.CalendarEvent.other.startWeekdayValidation"))];
                        }
                    return [];
                }
            }),
            new FrontendField("endDate", "", FrontendFieldType.date, { 
                mandatory: true,
                guiGroup: "startDate", 
                guiWidthInGroup: 1,                
                dateIntervalFromField: "startDate",
                dateIntervalEmptyAllowed: false,
                toForm: (value, _) => value ? Dates.add(value as Date, "day", -1) : null,
                fromForm: (value, _) => value ? Dates.add(value as Date, "day", 1) : null,
                onValueChanged: this.endDateChanged.bind(this),
                validator: (field, form) => {
                    let endDate = field.getValueDate();
                    //add Hour -12 wegen UTC Thematik. Hack, der behoben wird, wenn neue DateAPI verwendet wird
                    if (form.getValueBool("onlyWeekdays") && endDate != null && !Dates.isWeekday(Dates.add(endDate, "hour", -12))) {
                        return [new FrontendFieldError(field.id, this.app.t("entity.CalendarEvent.other.endWeekdayValidation"))];
                        }
                    return [];
                }
            }),
            new FrontendField("_2", "", FrontendFieldType.splitter),
            new FrontendField("plannedTime", "", FrontendFieldType.decimal, { 
                spinnerStep: 0.25,
                minValue: 0,
                fromForm: value => value ?? 0,
                guiGroup: "plannedTime", 
                guiWidthInGroup: 1,
                validator: (field, form) => {
                    let roles = form.getValue("roles") as any[];
                    return field.getValueNumber() > 0 && roles != null && (roles.length > 1 ||
                                    roles.length == 1 && roles[0].count > 1)
                                ? [new FrontendFieldError(field.id, this.app.t("entity.CalendarEvent.other.plannedTimeRolesValidation"))] : [];
                        }
            }),
            new FrontendField("doneTime", "", FrontendFieldType.decimal, { 
                spinnerStep: 0.25,
                minValue: 0,
                fromForm: value => value ?? 0,
                guiGroup: "plannedTime", 
                guiWidthInGroup: 1
            }),
            new FrontendField("_4", "", FrontendFieldType.splitter),
            new FrontendField("resources", "", FrontendFieldType.dropdownMulti, { 
                listItems: FrontendFieldListItem.make(this.resources, item => [item.id, item.title], true),
                dropdownCanFilter: true,
                fromForm: (value, _) => (value as number[]).map(id => this.item.resources.find(res => res.resourceId == id) ?? Objects.fromPlain(CalendarEventResource, {
                    resourceId: id
                })),
                toForm: (value, _) => (value as CalendarEventResource[]).map(r => r.resourceId)
            }),
            new FrontendField("roles", this.app.t("entity.CalendarEvent.other.roles"), FrontendFieldType.table, {
                mandatory: true,
                tableColumns: [
                    new TableColumn("responsibleId", this.app.t("entity.CalendarEventRole.fields.responsibleId"), TableCellType.int, -1, {
                        editable: true,
                        editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                            listItems: FrontendFieldListItem.make(this.users, item => [item.id, item.getFullName()], true, [[null, this.app.t("entity.CalendarEvent.other.noEmployee")]]),
                            dropdownCanFilter: true
                        }),
                        valueFormatter: e => this.users.find(item => item.id == e.value)?.getFullName() ?? this.app.t("entity.CalendarEvent.other.noEmployee")
                    }),  
                    new TableColumn("roleId", this.app.t("entity.CalendarEventRole.fields.roleId"), TableCellType.int, -1, {
                        editable: true,
                        editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                            listItems: FrontendFieldListItem.make(this.roles, item => [item.id, item.title], true, [[null, this.app.t("entity.CalendarEvent.other.noRole")]]),
                            dropdownCanFilter: true
                        }),
                        valueFormatter: e => this.roles.find(item => item.id == e.value)?.title ?? this.app.t("entity.CalendarEvent.other.noRole")
                    })
                ],
                fixedHeight: "150px",
                generateEmptyTableItem: () => new CalendarEventRole(),
                tableCanAddRow: true,
                tableCanRemoveRow: true
            }),
            ...ProjectPlanningTools.createReminderFields(this.app, () => this.form)
]));
    }

    async fillForm(): Promise<void> {
        await super.fillForm();
        this.form.getField("doneTime").enabled = this.editAllowed && this.item.isSetDoneTimeAllowed(this.service.getCurrentUser());
        if (this.isFinished || !this.editAllowed) {
            for (let field of this.form.getAllFields()) {
                field.enabled = false;
            }
        }
    }

    async initAdditionalData(): Promise<void> {
        this.projects = await this.service.getProjects();
        this.eventTemplates = await this.service.getEventTemplates(); 
        this.eventTypes = await this.service.getEventTypes();
        this.users = await this.service.getActiveUsers();
        this.roles = await this.service.getRoles();    
        this.resources = await this.service.getResources();  
        this.project = (await this.service.getProjects()).find(p => p.id == this.item.projectId);
        this.eventsOfProject = this.project != null ? await this.service.getEventsForCalendars([this.project.calendarId]) : [];
        let currentUser = this.service.getCurrentUser();
        if (this.isInsert()) {
            this.item.createdBy = currentUser?.getFullName();
            this.item.contactPersonId = currentUser?.id;
        }

        this.item.roles ??= [];
        this.item.subTasks ??= [];
        this.isFinished = this.item.finished;
        this.copy = Objects.cloneDeep(this.item);
    }

    getDialogButtons(): ButtonDefinition[] {
        let result = super.getDialogButtons();
        if (!this.editAllowed) {
            result = result.filter(b => b.id != "save");
        }    
        return Arrays.withoutNull([
            ...result,
            !this.isInsert() && this.item.isFinishAllowed(this.service.getCurrentUser()) ?new ButtonDefinition("finish", this.app.t("entity.CalendarEvent.other.finishButton"), true, "fas fa-check", "", "warn") : null,
            !this.isInsert() && this.item.isReopenAllowed(this.service.getCurrentUser()) ? new ButtonDefinition("open", this.app.t("entity.CalendarEvent.other.reopenButton"), true, "fas fa-check", "", "warn") : null,
            !this.isInsert() && !this.isCopy && this.item.isCopyAllowed(this.service.getCurrentUser()) ? new ButtonDefinition("copy", this.app.t("entity.CalendarEvent.other.copyButton"), true, "fas fa-copy", "", "info") : null,
            !this.isInsert() &&  this.item.isDeleteAllowed(this.service.getCurrentUser()) ? new ButtonDefinition("delete", this.app.t("general.delete"), true, "fas fa-trash", "", "danger") : null
        ]);      
    }

    async buttonClickedFromExtern(id: string): Promise<void> {
        if (id == "finish") {
            this.form.get(this.item);
            if (await this.service.finishEvent(this.item, this.service.getCurrentUser())) {
                this.form.fill(this.item);
                await this.save(id);
            }
        }
        else if (id == "copy") {
            await this.save(id);
        }
        else if (id == "open") {
            this.form.get(this.item);
            if (this.service.reopenEvent(this.item)) {
                this.form.fill(this.item);
                await this.save(id);
            }
        }
        else if (id == "delete") {
            await this.service.deleteEvent(this.item, () => this.close(), this);
        }
        else {
            await super.buttonClickedFromExtern(id);
        }
    }

    validateForm(buttonId?: string): boolean {
        return this.app.validateForm(this.form, () => {
            let roles = this.form.getValue("roles") as CalendarEventRole[];
            if (roles?.some(role => role.responsibleId == null && role.roleId == null)) {
                return [new FrontendFieldError("roles", this.app.t("entity.CalendarEvent.other.emptyRoleValidation"))];
            }
            if (buttonId == "copy" || this.isCopy) {
                let item = this.form.get(this.item, true);
                if (item.kind == this.copy.kind &&
                    item.title == this.copy.title &&
                    item.note == this.copy.note &&
                    item.type?.id == this.copy.type?.id &&
                    item.rolesEqual(this.copy) &&
                    Dates.eq(item.startDate, this.copy.startDate) &&
                    Dates.eq(item.endDate, this.copy.endDate)) {
                    return [new FrontendFieldError("title", this.app.t("entity.CalendarEvent.other.copyValidation"))];
                }
            }
            return [];
        });
    }

    async upsert(item: CalendarEvent, buttonId?: string): Promise<boolean> {
        return (await this.app.saveDataHandler2(new SaveDataHandler<boolean>({
            componentView: this,
            worker: async () => {
                if (buttonId == "copy") {
                    item = Objects.cloneDeep(item);
                    item.prepareForCopy();
                }
                let result = await RestEndpoint.main().upsert(item).query({ previewIfNeeded: true }).run(this.definition.apiEndpoint).get(CrudWithPreviewDto);
                if (!result.executed) {
                    if (!await this.app.dialog.yesNo(this.app.t("entity.CalendarEvent.other.previewWarning", { previewItems: Arrays.itemsToString(result.previewItems, "<br>") }), this.app.t("general.warning"))) {
                        return false;
                    }
                    await RestEndpoint.main().upsert(item).query({ previewIfNeeded: false }).run(this.definition.apiEndpoint).get(CrudWithPreviewDto);
                }

                await this.app.trackChanges();
                return true;
            },
            navigateBack: !this.multiInsert,
            isSuccess: result => result
        }))).data;
    }

    async projectIdChanged(): Promise<void> {
        let projectId = this.form.getValueNumber("projectId");
        this.project = this.projects.find(p => p.id == projectId);
        this.eventsOfProject = this.project != null ? await this.service.getEventsForCalendars([this.project.calendarId]) : [];
        this.form.getField("structurePath").listItems = FrontendFieldListItem.make(this.project?.structurePaths ?? [], item => [item, this.service.formatStructurePathString(item)], true);
        this.form.getField("startRelativeTo").listItems = this.getStartRelativeToEventIdListItems();
        this.startRelativeToChanged();
    }

    async eventTemplateChanged(): Promise<void> {
        let templateId = this.form.getValueNumber("template");
        let template = templateId == null ? new CalendarEventTemplate() : await ProjectPlanningTools.definitionCalendarEventTemplate.loadDataHandlerGet(templateId, this.app, this);
        this.form.setValue("title", template.title); 
        this.form.setValue("typeId", template.typeId);      
        this.form.setValue("note", template.description);
        if (templateId != null) {
            this.form.setValue("kind", template.isMilestone ? CalendarEventKind.milestone : CalendarEventKind.task);
            this.eventTypeChanged();
        }
        this.form.setValue("onlyWeekdays", template.onlyWeekdays);
        this.form.setValue("plannedTime", template.workingTime);  
        this.form.setValue("subTasks", Arrays.mapWithIndex(template.subTasks, (st, index) => Objects.fromPlain(CalendarEventSubTask, {
            order: index,
            title: st,
            finished: false
        })));  
        this.form.setValue("roles", Arrays.explode(template.roles, r => Utils.getRange(1, r.count).map(_ => Objects.fromPlain(CalendarEventRole, {
            roleId: r.roleId
        }))));  
        this.form.setValue("reminderType", template.reminderType, true);
        this.form.setValue("reminderDeltaMinutes", template.reminderDeltaMinutes);        
    }

    eventTypeChanged(): void {
        let eventType = this.form.getValueNumber("kind") as CalendarEventKind;
        this.updateDateFieldsEnabled();
        this.form.getField("_2").visible = eventType != CalendarEventKind.milestone;
        let plannedTimeVisible = eventType != CalendarEventKind.milestone && ProjectPlanningTools.userCanAccessPlanTime(this.service.getCurrentUser());
        this.form.getField("plannedTime").visible = plannedTimeVisible;
        this.form.getField("doneTime").guiGroup = plannedTimeVisible ? "plannedTime" : null;
        this.form.getField("doneTime").visible = eventType != CalendarEventKind.milestone;
        if (eventType == CalendarEventKind.milestone) {
            let endDate = this.form.getValueDate("endDate");
            this.form.setValue("startDate", endDate != null ? Dates.add(endDate, "day", -1) : null);
            this.form.setValue("plannedTime", 0);
            this.form.setValue("doneTime", 0);
        }
    }

    startRelativeDaysChanged(evt: FrontendFieldValueChangedEvent): void {
        if (evt.byUser) {
            this.updateRelativeDates();
        }
    }

    startRelativeToChanged(): void {
        this.updateRelativeDates();
    }

    lastValidStartDate: Date = null;

    startDateChanged(evt: FrontendFieldValueChangedEvent): void {
        let startDate = this.form.getValueDate("startDate");
        let endDate = this.form.getValueDate("endDate");
        let eventType = this.form.getValueNumber("kind") as CalendarEventKind;
        if (eventType == CalendarEventKind.milestone) {
            this.form.setValue("endDate", startDate != null ? Dates.add(startDate, "day", 1) : null);            
        }
        else if (startDate != null && endDate != null && this.lastValidStartDate != null && evt.byUser) {
            let diff = Dates.diffMillis(this.lastValidStartDate, startDate);
            this.form.setValue("endDate", Dates.add(endDate, "milli", diff));
        }
        if (Dates.is(startDate)) {
            this.lastValidStartDate = startDate;
        }
    }

    endDateChanged(): void {
        let eventType = this.form.getValueNumber("kind") as CalendarEventKind;
        if (eventType == CalendarEventKind.milestone) {
            let endDate = this.form.getValueDate("endDate");
            this.form.setValue("startDate", endDate != null ? Dates.add(endDate, "day", -1) : null);
        }
    }

    updateDateFieldsEnabled(): void {
        let datesAreRelative = this.form.getField("startRelativeTo").value != null;
        let eventType = this.form.getValueNumber("kind") as CalendarEventKind;
        let isMilestone = eventType == CalendarEventKind.milestone;
        this.form.getField("startDate").enabled = !datesAreRelative && !isMilestone;
        this.form.getField("endDate").enabled = !datesAreRelative;
    }

    updateRelativeDates(): void {
        let startRelativeTo = this.form.getField("startRelativeTo").value;
        this.form.getField("startRelativeDays").enabled = startRelativeTo != null;
        this.updateDateFieldsEnabled();
        if (this.project == null) {
            return;
        }
        let newStartDate = this.project.getEventStartDate(
            startRelativeTo instanceof CalendarEvent ? CalendarEventStartRelativeTo.task : (startRelativeTo as CalendarEventStartRelativeTo), 
            this.form.getValueNumber("startRelativeDays"),
            startRelativeTo instanceof CalendarEvent ? startRelativeTo.id : null, 
            this.form.getValueBool("onlyWeekdays"),
            this.eventsOfProject);
        if (newStartDate != null) {
            this.form.getField("startDate").setValue(newStartDate);    
        }

        this.startDateChanged(FrontendFieldValueChangedEvent.simple(this.form.getField("startDate"), true));   
    }

    getStartRelativeToEventIdListItems(): FrontendFieldListItem[] {
        return [
            ...FrontendFieldListItem.make(Utils.getEnumValues(CalendarEventStartRelativeTo).filter(v => (v as CalendarEventStartRelativeTo) != CalendarEventStartRelativeTo.task), item => [item, CalendarEventStartRelativeTo.getDisplayName(item, this.app)], false, [[null, this.app.t("entity.CalendarEvent.other.none")]]),
            ...FrontendFieldListItem.make(this.eventsOfProject.filter(e => e.id != this.item.id), item => [item, item.title + " (" + this.service.formatStructurePathString(item.structurePath) + ")"], false)
        ];
    }

}
